import React, { useState } from "react";
import {
  Modal,
  Stack,
  Textarea,
  Button,
  Text,
  Alert,
  Group,
  TextInput,
} from "@mantine/core";
import { Dropzone } from "@mantine/dropzone";
import { importKey, type KeyPair } from "../lib/keys";
import { QrScannerModal } from "./QrScannerModal";

type ImportKeyModalProps = {
  opened: boolean;
  onClose: () => void;
  onImported: (key: KeyPair) => void;
};

export function ImportKeyModal({
  opened,
  onClose,
  onImported,
}: ImportKeyModalProps) {
  const [privateKeyText, setPrivateKeyText] = useState("");
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [parsedKey, setParsedKey] = useState<KeyPair | null>(null);
  const [qrScannerOpen, setQrScannerOpen] = useState(false);

  const handleParse = async () => {
    setImporting(true);
    setError(null);
    try {
      const key = await importKey(privateKeyText);
      setParsedKey(key);
    } catch (e) {
      console.error(e);
      setError(
        "Failed to parse private key. Ensure it's a valid armored OpenPGP private key.",
      );
    } finally {
      setImporting(false);
    }
  };

  const handleConfirm = () => {
    if (parsedKey) {
      onImported(parsedKey);
      reset();
    }
  };

  const reset = () => {
    setPrivateKeyText("");
    setError(null);
    setParsedKey(null);
    onClose();
  };

  return (
    <>
      <Modal
        opened={opened}
        onClose={reset}
        title="Import Private Key"
        centered
        size="lg"
      >
        <Stack>
          {!parsedKey ? (
            <>
              <Alert color="orange" title="Warning">
                This should only be a key you have backed up and are moving to a
                new browser. Do not do this if asked by a third party or on a
                public machine.
              </Alert>

              <Dropzone
                onDrop={(files) => {
                  const file = files[0];
                  const reader = new FileReader();
                  reader.onload = (e) =>
                    setPrivateKeyText(e.target?.result as string);
                  reader.readAsText(file);
                }}
                maxSize={1024 * 1024}
                multiple={false}
                styles={{
                  root: {
                    border: "none",
                    padding: 0,
                    background: "transparent",
                  },
                  inner: { padding: 0 },
                }}
              >
                <div
                  className="premium-dropzone"
                  style={{
                    minHeight: 100,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Text size="sm" ta="center">
                    Drop key file here or <u>click</u> to select
                  </Text>
                </div>
              </Dropzone>

              <Group grow>
                <Button
                  variant="outline"
                  onClick={() => setQrScannerOpen(true)}
                >
                  Scan from QR Code
                </Button>
              </Group>

              <Text size="sm" c="dimmed">
                Or paste your ASCII-armored private key block below.
              </Text>
              <Textarea
                label="Private Key Block"
                placeholder="-----BEGIN PGP PRIVATE KEY BLOCK-----..."
                minRows={10}
                value={privateKeyText}
                onChange={(e) => setPrivateKeyText(e.target.value)}
              />
              {error && (
                <Alert color="red" title="Error">
                  {error}
                </Alert>
              )}
              <Button
                onClick={handleParse}
                loading={importing}
                disabled={!privateKeyText}
              >
                Parse Key
              </Button>
            </>
          ) : (
            <>
              <Alert color="blue" title="Key Parsed Successfully">
                <Stack gap="xs">
                  <Text size="sm">
                    <b>Name:</b> {parsedKey.name}
                  </Text>
                  <Text size="sm">
                    <b>Email:</b> {parsedKey.email}
                  </Text>
                  <Text size="sm">
                    <b>Key ID:</b> {parsedKey.id}
                  </Text>
                  <Text size="sm">
                    <b>Created:</b>{" "}
                    {new Date(parsedKey.createdAt).toLocaleDateString()}
                  </Text>
                  {parsedKey.expiresAt && (
                    <Text
                      size="sm"
                      c={
                        new Date(parsedKey.expiresAt) < new Date()
                          ? "red"
                          : "dimmed"
                      }
                    >
                      <b>Expires:</b>{" "}
                      {new Date(parsedKey.expiresAt).toLocaleDateString()}
                      {new Date(parsedKey.expiresAt) < new Date() &&
                        " (EXPIRED)"}
                    </Text>
                  )}
                </Stack>
              </Alert>
              <Group grow>
                <Button variant="default" onClick={() => setParsedKey(null)}>
                  Back
                </Button>
                <Button onClick={handleConfirm}>Import Key</Button>
              </Group>
            </>
          )}
        </Stack>
      </Modal>

      <QrScannerModal
        opened={qrScannerOpen}
        onClose={() => setQrScannerOpen(false)}
        onScan={(data) => {
          setPrivateKeyText(data);
          setQrScannerOpen(false);
        }}
      />
    </>
  );
}
